import React, { useState } from "react"
import Sticky from "react-stickynode"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { getApiUrlLocale } from "@/helpers"
import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import Footer from "@/containers/common/FooterNew"
import MetaData from "@/common/components/MetaData"
import SectionHeading from "@/common/components/SectionHeading"
import Button from "@/common/components/Button"
import Container from "@/common/components/UI/Container"
import { AppWrapper, ContentWrapper } from "@/containers/common/style"
import { COMPANY_DATA } from "@/common/data/CompanyData"
import IconCard from "@/common/components/IconCard"

const Company = ({ activeTag }: { activeTag?: string }) => {
  let parsedData = COMPANY_DATA
  if (activeTag != null) {
    parsedData = COMPANY_DATA.filter(d => d.tag.includes(activeTag))
  }
  return (
    <PartnersWrapper className="partnersWrapper">
      <Container className="partnerCards">
        {parsedData.map(({ image, alt, url }, index) => (
          <IconCard
            key={`${alt}_${index}`}
            img={image}
            alt={alt}
            url={url ?? ""}
          />
        ))}
      </Container>
    </PartnersWrapper>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_CMS_HOST
    }/api/media-coverages?populate=*&locale=${getApiUrlLocale(
      locale
    )}&sort[0]=date%3Adesc`,
    {
      headers: {
        "Cache-Control": "s-maxage=1, stale-while-revalidate"
      }
    }
  )
  const data = await res.json()

  return {
    props: {
      press: data,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

interface TabButtonsProps {
  tab: string
  id?: string
  title: string
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabButtons = ({
  tab,
  id,
  title,
  activeTab,
  setActiveTab
}: TabButtonsProps) => {
  return (
    <Button
      id={id}
      className="tabButton"
      type={activeTab === tab ? undefined : "secondary"}
      title={title}
      onClick={() => setActiveTab(tab)}
    />
  )
}

const Partners = () => {
  const { t } = useTranslation("common")
  const [activeTab, setActiveTab] = useState("PartnerAll")
  return (
    <div>
      <>
        <MetaData
          title={t("partner.meta.title")}
          description={t("partner.meta.description")}
          url={t("partners.meta.url")}
          image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
          languageAlternates={[
            [
              {
                hrefLang: "en-US",
                href: "https://www.kryptogo.com/partners"
              },
              {
                hrefLang: "zh-TW",
                href: "https://www.kryptogo.com/tw/partners"
              },
              {
                hrefLang: "zh-CN",
                href: "https://www.kryptogo.com/cn/partners"
              }
            ]
          ]}
        />

        <ResetCSS />
        {/* end of global and reset style */}

        {/* start app classic landing */}
        <AppWrapper>
          <Sticky top={0} innerZ={1000} activeClass="sticky-active">
            <NavbarNew />
          </Sticky>
          <TitleWrapper>
            <TitleBG />
            <SectionHeading
              title={t("partner.title")}
              description={t("partner.desc")}
            />
          </TitleWrapper>
          <Container>
            <ContentWrapper className="mobileScoll">
              <ButtonWrapper>
                <TabButtons
                  id="PartnerAll"
                  title={t("partner.all")}
                  tab="PartnerAll"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  id="Accelerator"
                  title={t("partner.tag1")}
                  tab="Accelerator"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  id="Cooperation"
                  title={t("partner.tag2")}
                  tab="Cooperation"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  id="business cooperation"
                  title={t("partner.tag3")}
                  tab="business cooperation"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </ButtonWrapper>

              {activeTab === "PartnerAll" && (
                <div>
                  <Company />
                </div>
              )}

              {activeTab === "Accelerator" && (
                <div>
                  <Company activeTag={"Accelerator"} />
                </div>
              )}
              {activeTab === "Cooperation" && (
                <div>
                  <Company activeTag="Cooperation" />
                </div>
              )}

              {activeTab === "business cooperation" && (
                <div>
                  <Company activeTag="business cooperation" />
                </div>
              )}

              {/* >>>>>>>>>>>>>>>>>>>>>>>以下是 聯繫表單 */}
            </ContentWrapper>
            {/* <SectionLine /> */}
          </Container>
          <Footer />
        </AppWrapper>
      </>
    </div>
  )
}

export default Partners

const ButtonWrapper = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  gap: 24px;

  @media (max-width: 800px) {
    justify-content: center !important;
    overflow: visible;
    flex-wrap: wrap;

    .tabButton {
      flex: 1 1 40%;
      padding: 10px 15px;
      min-width: 0px;
      background-color: none !important;
      box-shadow: none;
      transform: scale(1) !important;

      &:hover,
      &:active {
        box-shadow: none;
        transform: scale(1) !important;
      }

      &:nth-child(1) {
        flex: 1 1 100%;
      }
    }
  }
`

const TitleWrapper = styled.div`
  padding-top: 250px !important;
  padding-bottom: 100px !important;
  margin-bottom: -50px;
  position: relative;
  overflow: hidden;
  z-index: -1;

  h2,
  h4 {
    text-shadow: 0px 10px 20px $colorsBlack;
    max-width: 1200px !important;
    padding: 20px 20px 10px 20px;
    line-height: 1.5;
    font-weight: 600;
    font-size: 1rem;
    letter-spacing: 0.36px;
    color: rgba(255, 255, 255, 0.8);
  }

  h2 {
    line-height: 2.5rem;
    font-weight: 700;
    line-height: 2.5rem;
    font-weight: 700;
    font-size: 48px;
    line-height: 1.15;
    letter-spacing: 1;
    background: linear-gradient(to right, #ffffff, #ffc211);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow:
      0 0 20px rgba(255, 194, 17, 0.2),
      0 0 60px rgba(255, 194, 17, 0.5);
  }

  @media (max-width: 768px) {
    padding-top: 200px !important;

    h2 {
      font-size: 2rem;
    }

    h4 {
      font-size: 1rem;
      font-weight: 600;
    }
  }
  padding-top: 160px;
  @media (max-width: 768px) {
    padding-top: 100px;
  }
  @keyframes BannerSection {
    0% {
      background-size: 50%;
    }

    100% {
      background-size: 50%;
      background-position-x: 200%;
    }
  }
`

const TitleBG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -2;
  opacity: 0.5;
  background-size: cover;
  background-position: center;
  animation: BannerSection 100s infinite alternate;
  background:
    linear-gradient(#03122e, #03122e07 45%, #03122e 90%),
    url(/partners/partnersBanner.svg);
`

const ContactSection = styled.div`
  background:
    linear-gradient(#03122e 1%, rgba(0, 0, 0, 0) 80%),
    linear-gradient(0deg, #03122e 1%, rgb(0, 0, 0) 30%), rgb(0, 0, 0);
  margin-top: 60px;
  padding: 60px;
  padding-top: 120px;

  .contactBox {
    border: none;
    padding-top: 0px;
    box-shadow: none;
    width: 100%;
    max-width: 800px;
    padding: 0 !important;
  }

  @media (max-width: 678px) {
    padding: 30px;
    margin-top: 60px;
    padding-top: 60px;
  }

  .max {
    margin-bottom: 60px !important;
    width: 100%;
    text-align: center;
  }
`

const FormWrapper = styled.form`
  display: flex;
  max-width: 1000px;
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--textGap);
`
const FormContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0 var(--textGap);
  .required {
    color: ${themeGet("colors.primary")};
    margin-left: 4px;
  }
  div {
    margin: 1rem 0rem;
    flex: 1 48%;
    &.fullWidth {
      flex: 1 100%;
    }
    @media (max-width: 640px) {
      margin: 0 0 2rem;
      width: 100%;
    }
  }
  label {
    display: flex;
    flex-wrap: wrap;
    p {
      order: -1;
      transition: 0.3s ease;
    }
    &:hover {
      p {
        color: ${themeGet("colors.title")};
      }
    }
  }
  input,
  textarea {
    font-size: 20px;
    width: 100%;
    padding: 4px 8px;
    color: #fff;
    background-color: transparent;
    outline: none;
    border: none;
    border-bottom: 1px solid #fff;
    transition: 0.3s ease;
    &:focus {
      border-color: ${themeGet("colors.primary")};
      ~ p {
        color: ${themeGet("colors.primary")};
      }
    }
    @media (max-width: 640px) {
      padding: 0;
    }
  }
  .CheckBoxWrap {
    justify-content: center;
    padding: 20px 0;
    cursor: pointer;
    transition: 0.3s ease;
    flex-wrap: nowrap;
    @media (max-width: 768px) {
      padding: 0;
    }
    p {
      margin-top: 0;
      order: 2;
    }
    input[type="checkbox"] {
      width: auto !important;
      margin: 10px;
      &:focus ~ p {
        color: ${themeGet("colors.text")};
      }
    }
    a {
      color: ${themeGet("colors.primary")};
      margin-left: 10px;
      border-bottom: 1px solid;
      display: inline;
    }
  }
  button[type="submit"] {
    border-bottom: none;
    border-radius: 100px;
    margin: 15px auto;
  }
`
const PartnersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 2vh 0;
  .partnerCards {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 52px;
    margin-bottom: 50px;
  }
`
